import { RequestOptions, RequestOptionsWithBody } from "../models/ApiRequest";
import { ApiDataResponse } from "../models/ApiResponse";
import FetchClient from "../networking/FetchClient";
import { useAuthStore } from "./auth.store";

export const API_ROOT_TEST = "https://oo-api-gateway.test-1.fruster.se";
// export const API_ROOT_TEST = "https://api.origoworks.se";
export const ASSETS_ROOT_TEST = "https://oo-file-service.test-1.fruster.se";

export type MediaType = "image" | "application";

export const NO_NETWORK_ERROR = {
	status: 500,
	error: {
		code: "NO_NETWORK_ERROR",
		detail: "Det finns ingen anslutning till internet",
	},
};

interface RequestOptionsWithFile extends RequestOptions {
	body: any;
	mediaType: MediaType;
}

/**
 * Plain HTTP client singleton (no reactivity needed — tokens are read from the
 * auth store on each request and refreshed on 403).
 */
class ApiClient {
	apiRoot: string;
	assetsRoot: string;
	fetchClient: FetchClient;
	isConnected: boolean = true;

	constructor() {
		this.apiRoot = window.__env__?.API_ROOT || API_ROOT_TEST;
		this.assetsRoot = window.__env__?.ASSETS_ROOT || ASSETS_ROOT_TEST;
		// eslint-disable-next-line no-console
		console.log("Using API root: " + this.apiRoot);
		this.fetchClient = new FetchClient();
	}

	async get<T = any>(opts: RequestOptionsWithBody) {
		const path = this.apiRoot + opts.path;

		return await this.intercept<T>(
			() =>
				this.fetchClient.doRequest<T>({
					method: "get",
					path,
					query: opts.query,
					token: this.getAccessToken(opts.isAuthRequired),
				}),
			opts
		);
	}

	async getBlob(opts: RequestOptionsWithBody) {
		const path = this.apiRoot + opts.path;

		return this.fetchClient.doRequest({
			method: "get",
			path,
			query: opts.query,
			token: this.getAccessToken(opts.isAuthRequired),
			blobResponse: true,
		});
	}

	post<T = any>(opts: RequestOptionsWithBody) {
		const path = this.apiRoot + opts.path;

		return this.intercept<T>(
			() =>
				this.fetchClient.doRequest<T>({
					method: "post",
					path,
					body: opts.body,
					token: this.getAccessToken(opts.isAuthRequired),
				}),
			opts
		);
	}

	async put<T = any>(opts: RequestOptionsWithBody) {
		const path = this.apiRoot + opts.path;

		return await this.intercept<T>(
			() =>
				this.fetchClient.doRequest<T>({
					method: "put",
					path,
					body: opts.body,
					token: this.getAccessToken(opts.isAuthRequired),
				}),
			opts
		);
	}

	async delete<T = any>(opts: RequestOptionsWithBody) {
		const path = this.apiRoot + opts.path;

		return await this.intercept<T>(
			() =>
				this.fetchClient.doRequest<T>({
					method: "delete",
					path,
					body: opts.body,
					query: opts.query,
					token: this.getAccessToken(opts.isAuthRequired),
				}),
			opts
		);
	}

	async postFormData<T = any>(opts: RequestOptionsWithFile) {
		// Note: Use assets url instead of api root when doing uploads
		const path = this.assetsRoot + opts.path;
		const body = opts.body;
		const formDataBody = new FormData();

		for (let key in body) {
			formDataBody.append(key, body[key]);
		}

		return await this.intercept<T>(
			() =>
				this.fetchClient.doRequest<T>({
					...opts,
					path,
					body: formDataBody,
					method: "post",
					formData: true,
				}),
			opts
		);
	}

	private async intercept<T>(request: any, reqOpts: RequestOptions): Promise<ApiDataResponse<T>> {
		if (!this.isConnected) {
			throw NO_NETWORK_ERROR;
		}
		try {
			return await request();
		} catch (error: any) {
			if (reqOpts.isAuthRequired && error.status === 403) {
				return this.refreshAuthentication(request);
			} else {
				throw error;
			}
		}
	}

	private getAccessToken(isAuthRequired?: boolean) {
		if (isAuthRequired) {
			return useAuthStore.getState().accessToken;
		}
	}

	/**
	 * Refreshes the access token and retries the request. Parallel 403s share one
	 * refresh (see `useAuthStore.refreshAuthentication`); `callback` reads the token
	 * again, so the retry uses the new one.
	 */
	private async refreshAuthentication<T = any>(callback: () => ApiDataResponse<T>) {
		if (await useAuthStore.getState().refreshAuthentication()) {
			return await callback();
		}
		throw new Error("Failed to refresh token");
	}
}

export const apiStore = new ApiClient();
